(() => {
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const byId = (id) => document.getElementById(id);
  const parseJSON = (root, sel) => {
    const n = root.querySelector(sel);
    try { return n ? JSON.parse(n.textContent) : null; } catch { return null; }
  };
  const htmlToDoc = (html) => { const t=document.createElement('template'); t.innerHTML=html.trim(); return t.content; };
  const fetchSection = (sid, vid) => {
    const u = new URL(location.href);
    u.searchParams.set('section_id', sid);
    if (vid) u.searchParams.set('variant', vid);
    return fetch(u.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.text());
  };

  class PDPSuite {
    constructor(root){
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.priceEl = byId(`price-${this.sectionId}`);
      this.form = document.querySelector(`#shopify-section-${this.sectionId} product-form form, #shopify-section-${this.sectionId} form[action*="/cart/add"]`);
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.qtyInput = byId(`Quantity-${this.sectionId}`) || (this.form && this.form.querySelector('input[name="quantity"]'));
      this.product = parseJSON(root, 'script[data-product-json]') || {};
      this.selects = $$(root, '.pdp-option__select');
      this._cache = new Map();

      // Preserve original option labels so we can append status text (e.g. " - Sold Out")
      // without stacking duplicates across re-renders.
      this.selects.forEach(select => {
        Array.from(select.options).forEach(opt => {
          if (!opt.dataset.origLabel) opt.dataset.origLabel = (opt.textContent || '').trim();
        });
      });

      // Fast lookup map for variants
      this._variantByKey = new Map();
      (this.product.variants || []).forEach(v => {
        const key = (v.options || []).map(String).join('||');
        this._variantByKey.set(key, v);
      });

      this.selects.forEach(sel => sel.addEventListener('change', (e) => this.onOptionChange(e)));
      this.bindQtyClamp();

      const initial = this.resolveInitialVariant() || this.findVariantByOptions(this.selects.map(s => s.value));
      this.applyVariant(initial, { push:false });
      this.updateAvailability();
    }

    findVariantByOptions(options){
      const key = (options || []).map(String).join('||');
      return this._variantByKey.get(key) || null;
    }

    resolveInitialVariant(){
      const q = new URL(location.href).searchParams.get('variant');
      if (q){ const v=(this.product.variants||[]).find(v=>String(v.id)===String(q)); if (v) return v; }
      if (this.idInput && this.idInput.value){ const v=(this.product.variants||[]).find(v=>String(v.id)===String(this.idInput.value)); if (v) return v; }
      return (this.product.variants||[]).find(v=>v.available) || (this.product.variants||[])[0] || null;
    }

    onOptionChange(e){
      const selectedOptions = this.selects.map(s => s.value);
      const exact = this.findVariantByOptions(selectedOptions);

      // Allow selecting sold-out variants (needed for back-in-stock flows).
      // If the exact variant exists (available or not), apply it directly.
      if (exact) {
        this.applyVariant(exact, { push:true });
        this.updateAvailability();
        return;
      }

      const changedPos = e && e.target ? parseInt(e.target.dataset.optionPosition || '', 10) : NaN;
      const changedIdx = Number.isFinite(changedPos) ? (changedPos - 1) : NaN;

      const variants = this.product.variants || [];
      let candidate = null;

      if (Number.isFinite(changedIdx)) {
        // Prefer an available variant that matches all other selections.
        candidate = variants.find(v => v.available && (v.options || []).every((val, i) => {
          if (i === changedIdx) return true;
          return String(val) === String(selectedOptions[i]);
        }));
      }

      if (!candidate) candidate = variants.find(v => v.available) || variants[0] || null;

      if (candidate) {
        this.setSelectsFromVariant(candidate);
        this.applyVariant(candidate, { push:true });
      } else if (exact) {
        this.applyVariant(exact, { push:true });
      }

      this.updateAvailability();
    }

    setSelectsFromVariant(variant){
      if (!variant || !this.selects.length) return;
      (variant.options || []).forEach((val, i) => {
        const sel = this.selects[i];
        if (sel && String(sel.value) !== String(val)) sel.value = String(val);
      });
    }

    isOptionComboAvailable(options){
      const v = this.findVariantByOptions(options);
      return !!(v && v.available);
    }

    updateAvailability(){
      if (!this.selects.length) return;
      const current = this.selects.map(s => s.value);

      this.selects.forEach((select, idx) => {
        Array.from(select.options).forEach(opt => {
          const test = current.slice();
          test[idx] = opt.value;

          const v = this.findVariantByOptions(test);
          const isSoldOut = !!(v && v.available === false);
          const isMissing = !v;

          // Do NOT disable options (so customers can still select sold-out variants).
          // Instead, grey them out and append status text.
          const orig = opt.dataset.origLabel || (opt.textContent || '').trim();
          opt.dataset.origLabel = orig;

          if (isSoldOut) {
            opt.dataset.pdpStatus = 'soldout';
            opt.textContent = `${orig} - Sold Out`;
            opt.style.color = '#aaa';
          } else if (isMissing) {
            opt.dataset.pdpStatus = 'unavailable';
            opt.textContent = `${orig} - Unavailable`;
            opt.style.color = '#aaa';
          } else {
            opt.dataset.pdpStatus = '';
            opt.textContent = orig;
            opt.style.color = '';
          }
        });
      });
    }

    bindQtyClamp(){
      if (!this.qtyInput) return;
      const clamp = () => {
        const min = parseInt(this.qtyInput.getAttribute('min') || '1', 10) || 1;
        const maxAttr = this.qtyInput.getAttribute('max');
        const max = (maxAttr !== null && maxAttr !== '') ? parseInt(maxAttr, 10) : null;
        let val = parseInt(this.qtyInput.value || '1', 10) || 1;
        val = Math.max(val, min);
        if (max !== null && !Number.isNaN(max)) val = Math.min(val, max);
        this.qtyInput.value = String(val);
        this.updateStepperState();
      };
      this.qtyInput.addEventListener('input', clamp);
      this.qtyInput.addEventListener('change', clamp);

      // Also wire +/- buttons to update state consistently
      const wrap  = this.qtyInput.closest('.quantity');
      const minus = wrap && wrap.querySelector('button[name="minus"]');
      const plus  = wrap && wrap.querySelector('button[name="plus"]');
      if (minus) minus.addEventListener('click', () => setTimeout(()=>this.updateStepperState(), 0));
      if (plus)  plus.addEventListener('click', ()  => setTimeout(()=>this.updateStepperState(), 0));
    }

    updateStepperState(){
      if (!this.qtyInput) return;
      const wrap  = this.qtyInput.closest('.quantity');
      const minus = wrap && wrap.querySelector('button[name="minus"]');
      const plus  = wrap && wrap.querySelector('button[name="plus"]');
      const disabled = this.qtyInput.disabled;
      const min = parseInt(this.qtyInput.getAttribute('min') || '1', 10) || 1;
      const maxAttr = this.qtyInput.getAttribute('max');
      const max = (maxAttr !== null && maxAttr !== '') ? parseInt(maxAttr, 10) : null;
      const val = parseInt(this.qtyInput.value || '1', 10) || 1;

      if (minus) minus.disabled = disabled || (val <= min);
      if (plus)  plus.disabled  = disabled || (max !== null && !Number.isNaN(max) && val >= max);
    }

    setQtyDisabled(disabled){
      if (!this.qtyInput) return;
      this.qtyInput.disabled = !!disabled;
      this.updateStepperState();
    }

    setQtyValue(n){
      if (!this.qtyInput) return;
      this.qtyInput.value = String(n);
      try { this.qtyInput.dispatchEvent(new Event('change', { bubbles:true })); } catch {}
      this.updateStepperState();
    }

    updateURL(variantId, push){
      if (!variantId) return;
      const u = new URL(location.href); u.searchParams.set('variant', String(variantId));
      if (push) history.replaceState({}, '', u.toString());
    }

    async applyVariant(variant, { push=true } = {}){
      if (!variant) return;
      this.currentVariant = variant;

      if (this.idInput){ this.idInput.disabled=false; this.idInput.value=String(variant.id); }

      if (variant.available === false){ this.setQtyDisabled(true); this.setQtyValue(0); }
      else { this.setQtyDisabled(false); const min=parseInt(this.qtyInput.getAttribute('min')||'1',10)||1; this.setQtyValue(min); }

      this.updateAvailability();

      await this.refreshServerTruth(variant.id);

      // Sync media gallery and modal with selected variant image
      if (variant && variant.featured_media) {
        const mediaId = String(variant.featured_media.id);

        // Update main media galleries for this section
        document.querySelectorAll(`[id^="MediaGallery-${this.sectionId}"]`).forEach((gallery) => {
          if (typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(mediaId, false);
          }
        });

        // Reorder modal so the selected media is first
        const modalContent = document.querySelector(
          `#ProductModal-${this.sectionId} .product-media-modal__content`
        );
        if (modalContent) {
          const newMediaModal = modalContent.querySelector(
            `[data-media-id="${variant.featured_media.id}"]`
          );
          if (newMediaModal) {
            modalContent.prepend(newMediaModal);
          }
        }
      }

      this.updateURL(variant.id, push);
      try {
        document.dispatchEvent(new CustomEvent('variant:changed',{detail:{sectionId:this.sectionId, variant}}));
        document.dispatchEvent(new CustomEvent('variant:change', {detail:{sectionId:this.sectionId, variant}}));
      } catch {}
    }

    async refreshServerTruth(variantId){
      try {
        let html;
        if (this._cache.has(variantId)) {
          html = this._cache.get(variantId);
        } else {
          html = await fetchSection(this.sectionId, variantId);
          this._cache.set(variantId, html);
        }
        const doc = htmlToDoc(html);

        // Replace price/availability
        const srcPrice = doc.getElementById(`price-${this.sectionId}`);
        if (srcPrice && this.priceEl) this.priceEl.innerHTML = srcPrice.innerHTML;

        // Sync qty attributes from proxy first
        const newQty = doc.getElementById(`QuantityProxy-${this.sectionId}`) || doc.getElementById(`Quantity-${this.sectionId}`) || doc.querySelector(`#shopify-section-${this.sectionId} input[name="quantity"]`);
        if (newQty && this.qtyInput){
          ['min','max','step','disabled'].forEach(attr=>{
            if (newQty.hasAttribute(attr)) this.qtyInput.setAttribute(attr, newQty.getAttribute(attr));
            else this.qtyInput.removeAttribute(attr);
          });
          if (this.qtyInput.hasAttribute('disabled')) {
            this.setQtyValue(0);
          } else {
            const min = parseInt(this.qtyInput.getAttribute('min') || '1', 10) || 1;
            const maxAttr = this.qtyInput.getAttribute('max');
            const max = (maxAttr !== null && maxAttr !== '') ? parseInt(maxAttr, 10) : null;
            let val = parseInt(this.qtyInput.value || '1', 10) || 1;
            val = Math.max(val, min);
            if (max !== null && !Number.isNaN(max)) val = Math.min(val, max);
            this.setQtyValue(val);
          }
        }

        // Sync ATC disabled/label
        const srcBtn = doc.querySelector(`#product-form-${this.sectionId} button[type="submit"]`) || doc.querySelector('product-form button[type="submit"]');
        const dstBtn = this.form && this.form.querySelector('button[type="submit"]');
        if (srcBtn && dstBtn){
          dstBtn.disabled = srcBtn.disabled;
          dstBtn.setAttribute('aria-disabled', srcBtn.getAttribute('aria-disabled') || (srcBtn.disabled ? 'true' : 'false'));
          const srcLabel = srcBtn.querySelector('span');
          const dstLabel = dstBtn.querySelector('span');
          if (srcLabel && dstLabel && srcLabel.textContent.trim()) dstLabel.textContent = srcLabel.textContent.trim();
        }
      } catch(e) {
        console.warn('[PDP Suite] refreshServerTruth error', e);
      } finally {
        this.updateStepperState();
      }
    }
  }

  function initAll(){
    document.querySelectorAll('.pdp-suite[data-section-id]').forEach(root => {
      if (!root.__pdpSuite) root.__pdpSuite = new PDPSuite(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();